/**
 * volcengineAsr.js — 火山引擎 ASR 语音识别（二进制协议版）
 *
 * 参照 voice-ws-handler.ts 的主进程实现，将完整的 Volcengine 二进制协议
 * 移植到浏览器环境，包含 gzip 压缩、正确的消息类型/标志位、以及服务端响应解析。
 *
 * 协议格式（每个消息）：
 *   [4字节 Header] [4字节 PayloadSize(BE)] [Payload]
 *
 * Header 字节布局：
 *   Byte 0: [4-bit ProtocolVersion=1][4-bit HeaderWordCount=1]
 *   Byte 1: [4-bit MessageType][4-bit Flags]
 *   Byte 2: [4-bit Serialization][4-bit Compression]
 *   Byte 3: Reserved (0x00)
 *
 * 消息类型：
 *   0b0001 — FullRequest（完整配置请求，首帧）
 *   0b0010 — AudioOnly（纯音频数据）
 *
 * Flags：
 *   0b0000 — None（普通帧）
 *   0b0010 — Last（最后一帧）
 */

import pako from 'pako'

// ============================================================================
// Binary Protocol Constants
// ============================================================================

const PROTOCOL_VERSION = 0b0001
const HEADER_WORD_COUNT = 0b0001
const HEADER_SIZE = HEADER_WORD_COUNT * 4 // 4 bytes

const MSG_TYPE_FULL_REQUEST = 0b0001
const MSG_TYPE_AUDIO_ONLY = 0b0010
const MSG_TYPE_SERVER_RESPONSE = 0b1001
const MSG_TYPE_ERROR = 0b1111

const FLAG_NONE = 0b0000
const FLAG_LAST = 0b0010

const SERIAL_JSON = 0b0001
const SERIAL_NONE = 0b0000
const COMPRESS_GZIP = 0b0001

// ============================================================================
// Configuration
// ============================================================================

const WS_URL =
  (location.protocol === 'https:' ? 'wss:' : 'ws:') +
  '//' +
  location.host +
  '/bytedance-api/api/v3/sauc/bigmodel_async'
const HOTWORD_TABLE_ID = '2a353861-b4b4-444f-9096-87666790aee3'
const SAMPLE_RATE = 16000

// ============================================================================
// Protocol Helpers
// ============================================================================

/**
 * 创建 4 字节协议头
 */
function createHeader(msgType, flags, serialization, compression) {
  const buf = new ArrayBuffer(HEADER_SIZE)
  const view = new DataView(buf)
  view.setUint8(0, (PROTOCOL_VERSION << 4) | HEADER_WORD_COUNT)
  view.setUint8(1, (msgType << 4) | flags)
  view.setUint8(2, (serialization << 4) | compression)
  view.setUint8(3, 0x00)
  return new Uint8Array(buf)
}

/**
 * 拼接多个 Uint8Array
 */
function concatBuffers(...buffers) {
  const totalLength = buffers.reduce((sum, buf) => sum + buf.length, 0)
  const result = new Uint8Array(totalLength)
  let offset = 0
  for (const buf of buffers) {
    result.set(buf, offset)
    offset += buf.length
  }
  return result
}

/**
 * 构建首帧 FullRequest：JSON 配置 → gzip → header + size + payload
 */
function buildFullClientRequest() {
  const payload = {
    user: { uid: 'portal-user' },
    audio: {
      format: 'pcm',
      rate: SAMPLE_RATE,
      bits: 16,
      channel: 1
    },
    request: {
      model_name: 'bigmodel',
      enable_itn: true,
      enable_punc: true,
      enable_ddc: false,
      enable_nonstream: true,
      show_utterances: true,
      corpus: {
        boosting_table_id: HOTWORD_TABLE_ID
      }
    }
  }

  const jsonStr = JSON.stringify(payload)
  const compressed = pako.gzip(jsonStr)

  const header = createHeader(
    MSG_TYPE_FULL_REQUEST,
    FLAG_NONE,
    SERIAL_JSON,
    COMPRESS_GZIP
  )
  const sizeBuf = new ArrayBuffer(4)
  new DataView(sizeBuf).setUint32(0, compressed.length, false) // 大端序

  return concatBuffers(header, new Uint8Array(sizeBuf), new Uint8Array(compressed))
}

/**
 * 构建音频帧：PCM 数据 → gzip → header + size + payload
 * @param {Uint8Array} pcmData - 16-bit PCM 数据
 * @param {boolean} isLast - 是否为最后一帧
 */
function buildAudioRequest(pcmData, isLast) {
  const compressed = pako.gzip(pcmData)
  const flags = isLast ? FLAG_LAST : FLAG_NONE

  const header = createHeader(MSG_TYPE_AUDIO_ONLY, flags, SERIAL_NONE, COMPRESS_GZIP)
  const sizeBuf = new ArrayBuffer(4)
  new DataView(sizeBuf).setUint32(0, compressed.length, false)

  return concatBuffers(header, new Uint8Array(sizeBuf), compressed)
}

/**
 * 解析服务端二进制响应
 * @param {ArrayBuffer} data
 * @returns {{ msgType: number, text?: string, error?: { code: number, message: string } }}
 */
function parseServerResponse(data) {
  const bytes = new Uint8Array(data)
  const view = new DataView(data)
  const msgType = (bytes[1] >> 4) & 0x0f
  const flags = bytes[1] & 0x0f

  // 错误消息类型 0b1111
  if (msgType === MSG_TYPE_ERROR) {
    let offset = HEADER_SIZE
    const errCode = view.getUint32(offset, false)
    offset += 4
    const errSize = view.getUint32(offset, false)
    offset += 4
    const errMsg = new TextDecoder().decode(bytes.slice(offset, offset + errSize))
    return { msgType, error: { code: errCode, message: errMsg }}
  }

  // 只处理服务端结果响应 (0b1001)
  if (msgType !== MSG_TYPE_SERVER_RESPONSE) {
    return { msgType }
  }

  let offset = HEADER_SIZE
  // 某些 flags 包含 sequence 字段（4 字节）
  if (flags === 0b0001 || flags === 0b0011) {
    offset += 4
  }

  const payloadSize = view.getUint32(offset, false)
  offset += 4
  const payloadBytes = bytes.slice(offset, offset + payloadSize)

  let payloadStr
  try {
    payloadStr = pako.ungzip(payloadBytes, { to: 'string' })
  } catch {
    payloadStr = new TextDecoder().decode(payloadBytes)
  }

  const payload = JSON.parse(payloadStr)

  // 提取识别文本：支持 result.text（单段）和 result.utterance（多段组合）
  let text = ''
  if (payload?.result?.text) {
    text = payload.result.text
  } else if (payload?.result?.utterances) {
    text = payload.result.utterances
      .map((u) => u.text || '')
      .filter(Boolean)
      .join('')
  }

  return { msgType, text }
}

/**
 * 生成 UUID v4
 */
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

// ============================================================================
// VolcengineASR — 浏览器端 ASR 客户端
// ============================================================================

export class VolcengineASR {
  /**
   * @param {object} options
   * @param {(text: string) => void} options.onResult - 实时转写回调
   * @param {(err: Error|string) => void} options.onError - 错误回调
   * @param {(state: string) => void} options.onStateChange - 状态变化回调
   */
  constructor(options = {}) {
    this.ws = null
    this.stream = null
    this.audioContext = null
    this.processor = null
    this.source = null
    this.isRunning = false
    this._cleanupTimer = null
    this._resultText = ''

    this.onResult = options.onResult || (() => {})
    this.onError = options.onError || (() => {})
    this.onStateChange = options.onStateChange || (() => {})
  }

  get resultText() {
    return this._resultText
  }

  // ========================================================================
  // Public API
  // ========================================================================

  /**
   * 启动语音识别
   * 流程：获取麦克风 → 建立 WebSocket → 发送 FullRequest → 开始音频采集
   */
  async start() {
    if (this.isRunning) return

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      await this._connectWebSocket()
      this._sendFullRequest()
      this._startAudioCapture()
      this.isRunning = true
      this._resultText = ''
      this.onStateChange('recording')
    } catch (err) {
      this.onError(err)
      this._cleanup()
    }
  }

  /**
   * 停止语音识别
   * 发送最后一帧（FLAG_LAST）后关闭连接
   */
  stop() {
    if (!this.isRunning) return
    this.isRunning = false

    try {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this._sendLastAudioFrame()
      }
    } catch (e) {
      console.error('[VolcengineASR] stop error:', e)
    }

    // 确保最后一帧发送完毕再关闭连接
    this._cleanupTimer = setTimeout(() => {
      this._cleanup()
      this.onStateChange('stopped')
    }, 1000)
  }

  /**
   * 中止（不发送最后一帧，直接关闭）
   */
  abort() {
    this._resultText = ''
    this.isRunning = false
    clearTimeout(this._cleanupTimer)
    this._cleanup()
    this.onStateChange('stopped')
  }

  // ========================================================================
  // WebSocket Connection
  // ========================================================================

  _connectWebSocket() {
    return new Promise((resolve, reject) => {
      const requestId = generateUUID()
      const url = WS_URL + '?requestId=' + requestId
      // 通过反向代理连接火山引擎 ASR，认证头由 Nginx 或后端服务注入

      this.ws = new WebSocket(url)
      this.ws.binaryType = 'arraybuffer'

      this.ws.onopen = () => {
        resolve()
      }

      this.ws.onmessage = (event) => {
        try {
          const result = parseServerResponse(event.data)

          if (result.error) {
            console.error('[VolcengineASR] Server error:', result.error)
            this.onError(new Error(result.error.message))
            return
          }

          if (result.text !== undefined && result.text) {
            this._resultText = result.text
            this.onResult(result.text)
          }
        } catch (e) {
          console.error('[VolcengineASR] Parse error:', e)
        }
      }

      this.ws.onerror = (err) => {
        console.log(err)
        reject(new Error('WebSocket 连接失败'))
      }

      this.ws.onclose = (event) => {
        // 如果还在运行中但 WS 意外关闭，触发清理
        if (this.isRunning) {
          this.isRunning = false
          clearTimeout(this._cleanupTimer)
          this._cleanup()
          this.onStateChange('stopped')
        }
      }
    })
  }

  // ========================================================================
  // Protocol Send Helpers
  // ========================================================================

  _sendFullRequest() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
    try {
      const frame = buildFullClientRequest()
      this.ws.send(frame)
    } catch (e) {
      console.error('[VolcengineASR] Failed to send full request:', e)
      this.onError(e)
    }
  }

  _sendLastAudioFrame() {
    try {
      // 发送一个空的最后一帧（无 PCM 数据，仅标志位）
      const emptyPcm = new Uint8Array(0)
      const frame = buildAudioRequest(emptyPcm, true)
      this.ws.send(frame)
    } catch (e) {
      console.error('[VolcengineASR] Failed to send last frame:', e)
    }
  }

  // ========================================================================
  // Audio Capture
  // ========================================================================

  _startAudioCapture() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: SAMPLE_RATE
      })

      this.source = this.audioContext.createMediaStreamSource(this.stream)
      this.processor = this.audioContext.createScriptProcessor(4096, 1, 1)

      this.source.connect(this.processor)
      this.processor.connect(this.audioContext.destination)

      this.processor.onaudioprocess = (event) => {
        if (!this.isRunning || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
          return
        }

        try {
          const input = event.inputBuffer.getChannelData(0)
          const pcm16 = this._floatTo16BitPCM(input)
          const frame = buildAudioRequest(pcm16, false)
          this.ws.send(frame)
        } catch (e) {
          console.error('[VolcengineASR] Audio send error:', e)
        }
      }
    } catch (e) {
      console.error('[VolcengineASR] Audio capture init error:', e)
      this.onError(e)
    }
  }

  /**
   * Float32 PCM → 16-bit PCM
   */
  _floatTo16BitPCM(floatArray) {
    const buf = new ArrayBuffer(floatArray.length * 2)
    const view = new DataView(buf)
    for (let i = 0; i < floatArray.length; i++) {
      const s = Math.max(-1, Math.min(1, floatArray[i]))
      view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true) // little-endian
    }
    return new Uint8Array(buf)
  }

  // ========================================================================
  // Cleanup
  // ========================================================================

  _cleanup() {
    clearTimeout(this._cleanupTimer)
    this._cleanupTimer = null

    // 断开音频处理器
    if (this.processor) {
      try {
        this.processor.disconnect()
      } catch { /* ignore */ }
      this.processor = null
    }

    if (this.source) {
      try {
        this.source.disconnect()
      } catch { /* ignore */ }
      this.source = null
    }

    if (this.audioContext) {
      try {
        this.audioContext.close()
      } catch { /* ignore */ }
      this.audioContext = null
    }

    // 停止所有音轨
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop())
      this.stream = null
    }

    // 关闭 WebSocket
    if (this.ws) {
      try {
        if (
          this.ws.readyState === WebSocket.OPEN ||
          this.ws.readyState === WebSocket.CONNECTING
        ) {
          this.ws.close()
        }
      } catch { /* ignore */ }
      this.ws = null
    }
  }
}
